import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Heart, Minus, Plus, Sparkles, ShoppingCart } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import OptimizedImage from "./OptimizedImage";
import { Link } from "react-router-dom";
import { useState } from "react";
import AuthDialog from "./AuthDialog";
import { ActiveOffer } from "@/types/offer";
import { getNormalizedProductPrice } from "@/services/productService";
import OfferCountdown from "./OfferCountdown";
import { getProductPath } from "@/lib/productUrls";
import { captureCartAddSource, playCartAddFeedback } from "@/lib/cartAddFeedback";
import { useBraceletGift } from "@/hooks/useBraceletGift";

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  appPrice?: number;
  effectivePrice?: number;
  offerPrice?: number;
  offerDiscountAmount?: number;
  hasActiveOffer?: boolean;
  activeOffer?: ActiveOffer;
  image: string;
  category?: string;
  categorySlug?: string;
  productSlug?: string;
  badge?: string;
  rating?: number;
  description?: string;
  benefits?: string[];
  size?: "default" | "compact" | "promotion";
  priority?: boolean;
}

const tagShape = { clipPath: "polygon(0 0, 100% 0, 100% 73%, 50% 100%, 0 73%)" };

const DiscountTag = ({ amount, compact }: { amount: string; compact: boolean }) => (
  <span className={`relative inline-flex shrink-0 items-start justify-center drop-shadow-[0_3px_3px_rgba(69,39,11,0.4)] ${compact ? 'h-[36px] w-[32px] sm:h-[43px] sm:w-[40px]' : 'h-[42px] w-[38px] sm:h-[56px] sm:w-[50px]'}`} aria-label={`${amount} off`}>
    <span className="absolute inset-0 bg-[#bf8a34]" style={tagShape} aria-hidden="true" />
    <span className="relative m-px flex h-[calc(100%-2px)] w-[calc(100%-2px)] flex-col items-center bg-[#fff8e8] pt-0.5 text-[#643415] sm:m-[2px] sm:h-[calc(100%-4px)] sm:w-[calc(100%-4px)] sm:pt-1" style={tagShape} aria-hidden="true">
      <strong className={`whitespace-nowrap font-extrabold leading-none ${compact ? 'text-[8px] sm:text-[10px]' : 'text-[10px] sm:text-[13px]'}`}>{amount}</strong>
      <span className={`mt-0.5 font-bold leading-none tracking-wide ${compact ? 'text-[7px] sm:text-[8px]' : 'text-[7px] sm:text-[9px]'}`}>OFF</span>
    </span>
  </span>
);

const ProductCard = ({
  id,
  name,
  price,
  originalPrice,
  appPrice,
  effectivePrice,
  offerPrice,
  offerDiscountAmount,
  hasActiveOffer,
  activeOffer,
  image,
  category,
  categorySlug,
  productSlug,
  badge,
  rating,
  description,
  benefits,
  size = "default",
  priority = false,
}: ProductCardProps) => {
  const { addToCart, cart, updateQuantity } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const braceletGift = useBraceletGift();
  const giftEligible = braceletGift?.eligibleProductIds.includes(id) ?? false;

  const pricing = getNormalizedProductPrice({
    price,
    appPrice,
    originalPrice,
    effectivePrice,
    offerPrice,
    offerDiscountAmount,
    hasActiveOffer,
    activeOffer,
  });

  const normalDiscount = Math.round(
    ((pricing.originalPrice - pricing.basePrice) / (pricing.originalPrice || 1)) * 100
  );
  const inWishlist = isInWishlist(id);
  const cartItem = cart.find((item) => item.id === id);
  const quantityInCart = cartItem?.quantity || 0;
  const productPath = getProductPath({ id, categorySlug, productSlug });

  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(id);
    } else {
      addToWishlist(
        { id, name, price: pricing.finalPrice, originalPrice: pricing.originalPrice, image, badge, category, categorySlug, productSlug, rating: rating ?? 0, description },
        () => setAuthDialogOpen(true)
      );
    }
  };

  const handleAddToCart = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const source = captureCartAddSource(event.currentTarget);
    setIsAddingToCart(true);
    try {
      const added = await addToCart(
        {
          id,
          productId: id,
          name,
          price: pricing.finalPrice,
          appPrice: pricing.basePrice,
          originalPrice: pricing.originalPrice,
          image,
          category,
          categorySlug,
          productSlug,
          description,
        },
        () => setAuthDialogOpen(true)
      );
      if (added) playCartAddFeedback(source, name);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleIncrement = () => {
    updateQuantity(id, quantityInCart + 1);
  };

  const handleDecrement = () => {
    updateQuantity(id, quantityInCart - 1);
  };

  const isCompact = size === "compact";
  const isPromotion = size === "promotion";

  // Offer discount badge text formatting
  let offerBadgeText = "";
  if (pricing.hasActiveOffer && activeOffer) {
    if (activeOffer.discountType === "FLAT") {
      offerBadgeText = `₹${activeOffer.discountValue} OFF`;
    } else if (activeOffer.discountType === "PERCENT") {
      if (activeOffer.maxDiscount && pricing.discountAmount < Math.round((pricing.basePrice * activeOffer.discountValue) / 100)) {
        offerBadgeText = `₹${pricing.discountAmount} OFF`;
      } else {
        offerBadgeText = `${activeOffer.discountValue}% OFF`;
      }
    }
  }

  return (
    <Card className={`group overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col justify-between ${isPromotion ? 'border border-[#eadfcd] bg-white' : 'border-0 bg-gray-50'}`}>
      <div>
        <div
          className={`relative overflow-hidden ${
            isCompact ? "m-1.5 rounded-lg aspect-[3/2]" : isPromotion ? "m-2 rounded-lg aspect-square" : "m-3 md:m-4 rounded-2xl aspect-square"
          }`}
        >
          <Link to={productPath} className="block h-full w-full cursor-pointer">
            <OptimizedImage
              src={image}
              alt={name}
              priority={priority}
              sizes={isCompact ? "160px" : isPromotion ? "(max-width: 640px) 220px, (max-width: 1024px) 32vw, 220px" : "(max-width: 640px) 46vw, (max-width: 1024px) 30vw, 280px"}
              className="h-full w-full object-cover"
            />
            <div
              className="absolute right-0 top-0 z-10 flex flex-col items-end gap-1"
            >
              {pricing.hasActiveOffer && offerBadgeText ? (
                <DiscountTag amount={offerBadgeText.replace(/\s+OFF$/, '')} compact={isCompact} />
              ) : (
                <>
                  {normalDiscount > 0 && (
                    <DiscountTag amount={`${normalDiscount}%`} compact={isCompact} />
                  )}
                </>
              )}
            </div>

            {/* Bottom Overlay Row: Timer on Left, Rating on Right */}
            <div className="absolute inset-x-1.5 bottom-1.5 z-20 flex items-center justify-between gap-1 sm:inset-x-2 sm:bottom-2 pointer-events-none">
              <div className="pointer-events-auto min-w-0">
                {pricing.hasActiveOffer && (activeOffer?.expiresAt || activeOffer?.startsAt) && (
                  <OfferCountdown
                    startsAt={activeOffer.startsAt}
                    expiresAt={activeOffer.expiresAt}
                    compact={true}
                  />
                )}
              </div>
              {!isPromotion && <div className="pointer-events-auto shrink-0">
                <div className="inline-flex h-5 items-center gap-0.5 whitespace-nowrap rounded-full border border-amber-200 bg-white/95 px-1 text-[8px] font-bold leading-none text-neutral-900 shadow-sm backdrop-blur-sm sm:h-6 sm:px-1.5 sm:text-[9px]">
                  <Star className="h-2 w-2 sm:h-2.5 sm:w-2.5 fill-yellow-400 text-yellow-400 shrink-0" />
                  <span>{rating ?? 4.95}</span>
                </div>
              </div>}
            </div>
          </Link>
          <button
            type="button"
            onClick={handleWishlistToggle}
            className={`absolute z-30 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-md flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-800 ${isCompact ? 'min-h-6 min-w-6' : isPromotion ? 'min-h-10 min-w-10' : 'min-h-8 min-w-8'} ${
              isCompact ? 'top-1 left-1 p-1' : 'top-2 left-2 p-2'
            }`}
            aria-label={inWishlist ? `Remove ${name} from wishlist` : `Add ${name} to wishlist`}
          >
            <Heart
              className={`${isCompact ? "h-2.5 w-2.5" : "h-4 w-4"} ${
                inWishlist ? "fill-red-500 text-red-500" : "text-gray-600"
              }`}
            />
          </button>
        </div>
        <CardContent className={`text-center ${isCompact ? "p-1 pb-0" : isPromotion ? 'px-3 pb-2 pt-1' : "p-3 md:p-4"}`}>
          {giftEligible && <span className="mb-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-950"><Sparkles className="h-3 w-3" aria-hidden="true" /> Gift eligible</span>}
          <Link to={productPath} className="block">
            <h3
              className={`font-semibold text-foreground leading-snug hover:text-primary transition-colors ${
                isCompact
                  ? "mb-0.5 text-[10px] line-clamp-1"
                  : isPromotion
                    ? "mb-2 min-h-10 text-sm line-clamp-2"
                  : "mb-2 text-sm md:text-base line-clamp-2 min-h-[2.5rem]"
              }`}
            >
              {name}
            </h3>
          </Link>

          {/* Pricing Section */}
          <div className={`flex flex-col items-center justify-center ${isCompact ? "" : isPromotion ? 'space-y-0.5' : "mb-2 space-y-0.5"}`}>
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              <p className={`font-bold text-foreground ${isCompact ? "text-xs" : isPromotion ? 'text-base' : "text-xl md:text-2xl"}`}>
                ₹{pricing.finalPrice}
              </p>
              {pricing.hasActiveOffer ? (
                <p
                  className={`text-muted-foreground line-through ${
                    isCompact ? "text-[8px]" : isPromotion ? 'text-xs' : "text-sm md:text-base"
                  }`}
                >
                  ₹{pricing.basePrice}
                </p>
              ) : (
                pricing.originalPrice > pricing.basePrice && (
                  <p
                    className={`text-muted-foreground line-through ${
                      isCompact ? "text-[8px]" : isPromotion ? 'text-xs' : "text-sm md:text-base"
                    }`}
                  >
                    ₹{pricing.originalPrice}
                  </p>
                )
              )}
            </div>

            {pricing.hasActiveOffer && pricing.discountAmount > 0 && !isCompact && (
              <p className="text-[11px] font-semibold text-emerald-700">
                You save ₹{pricing.discountAmount}
              </p>
            )}
          </div>
        </CardContent>
      </div>

      <CardFooter className={`${isCompact ? "p-1 pt-0" : isPromotion ? 'px-3 pb-3 pt-0' : "p-3 pt-0 md:p-4 md:pt-0"}`}>
        {quantityInCart > 0 ? (
          <div
            className={`w-full flex items-center justify-between text-white font-bold ${isPromotion ? 'bg-amber-900' : 'bg-gradient-to-r from-orange-600 to-yellow-600'} ${
              isCompact ? "rounded-md px-1" : isPromotion ? 'rounded-lg px-1' : "rounded-xl px-3"
            }`}
          >
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-white/20 ${isCompact ? "h-6 w-6" : isPromotion ? 'h-11 w-11' : "h-10 w-10"}`}
              onClick={handleDecrement}
              aria-label={`Remove one ${name}`}
            >
              <Minus className={isCompact ? "h-3 w-3" : "h-4 w-4"} />
            </Button>
            <span className={`${isCompact ? "text-xs" : isPromotion ? 'text-base' : "text-lg"}`}>{quantityInCart}</span>
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-white/20 ${isCompact ? "h-6 w-6" : isPromotion ? 'h-11 w-11' : "h-10 w-10"}`}
              onClick={handleIncrement}
              aria-label={`Add one ${name}`}
            >
              <Plus className={isCompact ? "h-3 w-3" : "h-4 w-4"} />
            </Button>
          </div>
        ) : (
          <Button
            className={`w-full flex items-center justify-center gap-1 text-white font-bold uppercase tracking-wide ${isPromotion ? 'bg-amber-900 hover:bg-amber-950' : 'bg-gradient-to-r from-orange-600 to-yellow-600 hover:opacity-90'} ${
              isCompact
                ? "text-[8px] py-1 h-auto rounded-md"
                : isPromotion
                  ? 'min-h-11 rounded-lg px-2 py-2 text-xs'
                : "text-xs md:text-sm py-3 md:py-4 h-auto rounded-xl"
            }`}
            onClick={handleAddToCart}
            disabled={isAddingToCart}
          >
            <ShoppingCart className={isCompact ? "h-3 w-3" : "h-4 w-4"} />
            {isAddingToCart ? "Adding…" : "ADD TO CART"}
          </Button>
        )}
      </CardFooter>
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </Card>
  );
};

export default ProductCard;
